import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, Plus, Trash2, Edit, Briefcase, Calendar, Factory, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { getEntityId } from '@/lib/adminUtils';

const AdmExperience = () => {
  const { portfolioData, addExperience, updateExperience, deleteExperience } = usePortfolio();
  const [experience, setExperience] = useState(portfolioData?.experience || []);
  const [isSaving, setIsSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [modifiedExperience, setModifiedExperience] = useState(new Set());

  // Update local state when portfolioData changes
  useEffect(() => {
    setExperience(portfolioData?.experience || []);
    setModifiedExperience(new Set());
  }, [portfolioData]);

  const initialExperienceState = {
    title: '',
    company: '',
    period: '',
    location: '',
    achievements: []
  };

  const [newExperience, setNewExperience] = useState(initialExperienceState);

  const handleSave = async () => {
    if (modifiedExperience.size === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }

    setIsSaving(true);
    try {
      const savePromises = experience
        .filter((exp) => modifiedExperience.has(getEntityId(exp)))
        .map((exp) => updateExperience(getEntityId(exp), exp));

      const results = await Promise.all(savePromises);
      const allSuccessful = results.every((result) => result?.success !== false);
      if (allSuccessful) {
        setModifiedExperience(new Set());
        toast.success('Expérience sauvegardée avec succès');
      } else {
        const failed = results.find((result) => result?.success === false);
        toast.error(failed?.message || 'Erreur lors de la sauvegarde de l\'expérience');
      }
    } catch (error) {
      // console.error('Error saving experience:', error);
      toast.error('Erreur lors de la sauvegarde de l\'expérience');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setExperience(portfolioData?.experience || []);
    setEditingItem(null);
    setShowAddForm(false);
    setNewExperience(initialExperienceState);
    setModifiedExperience(new Set());
    toast.success('Liste réinitialisée');
  };

  const handleExperienceChange = (expId, field, value) => {
    setExperience((prev) =>
      prev.map((exp) => (getEntityId(exp) === expId ? { ...exp, [field]: value } : exp))
    );
    setModifiedExperience((prev) => new Set([...prev, expId]));
  };

  const handleAchievementsChange = (expId, value) => {
    const achievements = value.split('\n').map(achievement => achievement.trim()).filter(achievement => achievement);
    handleExperienceChange(expId, 'achievements', achievements);
  };

  const handleExperienceBlur = async (expId) => {
    if (!modifiedExperience.has(expId) || editingItem !== expId) return;
    const exp = experience.find((e) => getEntityId(e) === expId);
    if (!exp) return;

    const result = await updateExperience(expId, exp);
    if (result?.success) {
      setModifiedExperience((prev) => {
        const newSet = new Set(prev);
        newSet.delete(expId);
        return newSet;
      });
      toast.success('Expérience enregistrée');
    } else {
      toast.error(result?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleAddExperience = async () => {
    if (!newExperience.title?.trim() || !newExperience.company?.trim() || !newExperience.period?.trim()) {
      toast.error('Renseignez au minimum le titre, l\'entreprise et la période');
      return;
    }

    try {
      const result = await addExperience(newExperience);
      if (result?.success) {
        setNewExperience(initialExperienceState);
        setShowAddForm(false);
        if (result.data) {
          setExperience((prev) => [...prev, result.data]);
        }
        toast.success('Expérience ajoutée avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de l\'ajout de l\'expérience');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de l\'expérience');
    }
  };

  const handleDeleteExperience = async (expId) => {
    try {
      const result = await deleteExperience(expId);
      if (result?.success) {
        setExperience((prev) => prev.filter((exp) => getEntityId(exp) !== expId));
        toast.success('Expérience supprimée avec succès');
      } else {
        toast.error(result?.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression de l\'expérience');
    }
  };

  const startEditExperience = (exp) => {
    setEditingItem(getEntityId(exp));
  };

  const finishEditExperience = () => {
    setEditingItem(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <Factory className="w-6 h-6 mr-2" />
          Gestion de l'Expérience
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-gray-600 dark:text-gray-400">
          {experience.length} expérience(s) professionnelle(s)
        </p>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="w-4 h-4 mr-2" />
          Ajouter une expérience
        </Button>
      </div>

      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>Nouvelle Expérience Professionnelle</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-title" className="flex items-center mb-1">Titre du poste</Label>
                <Input
                  id="new-title"
                  value={newExperience.title}
                  onChange={(e) => setNewExperience(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ingénieur Développeur"
                />
              </div>
              <div>
                <Label htmlFor="new-company" className="flex items-center mb-1">Entreprise</Label>
                <Input
                  id="new-company"
                  value={newExperience.company}
                  onChange={(e) => setNewExperience(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="Nom de l'entreprise"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="new-period" className="flex items-center mb-1">
                  <Calendar className="w-4 h-4 mr-1" />
                  Période
                </Label>
                <Input
                  id="new-period"
                  value={newExperience.period}
                  onChange={(e) => setNewExperience(prev => ({ ...prev, period: e.target.value }))}
                  placeholder="Janvier 2020 - Présent"
                />
              </div>
              <div>
                <Label htmlFor="new-location" className="flex items-center mb-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  Localisation
                </Label>
                <Input
                  id="new-location"
                  value={newExperience.location}
                  onChange={(e) => setNewExperience(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Ville, Pays"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="new-achievements" className="flex items-center mb-1">Réalisations (une par ligne)</Label>
              <Textarea
                id="new-achievements"
                value={(newExperience.achievements || []).join('\n')}
                onChange={(e) => setNewExperience(prev => ({ 
                  ...prev, 
                  achievements: e.target.value.split('\n').map(achievement => achievement.trim()).filter(achievement => achievement)
                }))}
                placeholder="Réalisation 1&#10;Réalisation 2&#10;Réalisation 3"
                rows={4}
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={handleAddExperience}>Ajouter l'expérience</Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {experience.map((exp) => {
          const expId = getEntityId(exp);
          const achievements = exp.achievements || [];
          return (
          <Card key={expId ?? exp.title}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Briefcase className="w-5 h-5 mr-2" />
                  {exp.title} - {exp.company}
                </CardTitle>
                <div className="flex space-x-2">
                  {editingItem === expId ? (
                    <Button variant="outline" size="sm" onClick={finishEditExperience}>
                      Terminer
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => startEditExperience(exp)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDeleteExperience(expId)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {exp.period}
                </span>
                <span className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {exp.location}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`title-${expId}`} className="flex items-center mb-1">Titre du poste</Label>
                  <Input
                    id={`title-${expId}`}
                    value={exp.title || ''}
                    onChange={(e) => handleExperienceChange(expId, 'title', e.target.value)}
                    onBlur={() => handleExperienceBlur(expId)}
                    disabled={editingItem !== expId}
                  />
                </div>
                <div>
                  <Label htmlFor={`company-${expId}`} className="flex items-center mb-1">Entreprise</Label>
                  <Input
                    id={`company-${expId}`}
                    value={exp.company || ''}
                    onChange={(e) => handleExperienceChange(expId, 'company', e.target.value)}
                    onBlur={() => handleExperienceBlur(expId)}
                    disabled={editingItem !== expId}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`period-${expId}`} className="flex items-center mb-1">
                    <Calendar className="w-4 h-4 mr-1" />
                    Période
                  </Label>
                  <Input
                    id={`period-${expId}`}
                    value={exp.period || ''}
                    onChange={(e) => handleExperienceChange(expId, 'period', e.target.value)}
                    onBlur={() => handleExperienceBlur(expId)}
                    disabled={editingItem !== expId}
                  />
                </div>
                <div>
                  <Label htmlFor={`location-${expId}`} className="flex items-center mb-1">
                    <MapPin className="w-4 h-4 mr-1" />
                    Localisation
                  </Label>
                  <Input
                    id={`location-${expId}`}
                    value={exp.location || ''}
                    onChange={(e) => handleExperienceChange(expId, 'location', e.target.value)}
                    onBlur={() => handleExperienceBlur(expId)}
                    disabled={editingItem !== expId}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor={`achievements-${expId}`} className="flex items-center mb-1">Réalisations (une par ligne)</Label>
                <Textarea
                  id={`achievements-${expId}`}
                  value={achievements.join('\n')}
                  onChange={(e) => handleAchievementsChange(expId, e.target.value)}
                  onBlur={() => handleExperienceBlur(expId)}
                  disabled={editingItem !== expId}
                  rows={6}
                />
              </div>
              {editingItem !== expId && achievements.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Aperçu des réalisations:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-400">
                    {achievements.map((achievement, index) => (
                      <li key={index}>{achievement}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        );
        })}
      </div>

      {experience.length === 0 && !showAddForm && (
        <Card>
          <CardContent className="text-center py-8">
            <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Aucune expérience professionnelle
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Commencez par ajouter votre première expérience professionnelle
            </p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une expérience
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdmExperience;